import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Flag, Shield, Sparkles, Search, X, Camera, Save, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'flag', label: 'Flag Football', accent: 'bg-light-blue', accentText: 'text-light-blue', Icon: Flag },
  { key: 'tackle', label: 'Tackle Football', accent: 'bg-navy', accentText: 'text-navy', Icon: Shield },
  { key: 'cheer', label: 'Cheer', accent: 'bg-red', accentText: 'text-red', Icon: Sparkles },
];

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const AdminSpotlightsPage = () => {
  const navigate = useNavigate();

  const [activeSpotlights, setActiveSpotlights] = useState({ flag: null, tackle: null, cheer: null });
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  // form state
  const [category, setCategory] = useState('flag');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [writeup, setWriteup] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [imageData, setImageData] = useState(null); // { base64, filename, content_type }
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState(null);

  const authConfig = useMemo(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('pcc_auth_token') : null;
    return token ? { headers: { Authorization: `Bearer ${token}` }, withCredentials: true } : { withCredentials: true };
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [spotsRes, playersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/spotlights`, authConfig),
        axios.get(`${API_URL}/api/admin/spotlights/players`, authConfig),
      ]);
      const map = { flag: null, tackle: null, cheer: null };
      (spotsRes.data || []).forEach(row => { map[row.category] = row.spotlight; });
      setActiveSpotlights(map);
      setPlayers(playersRes.data || []);
    } catch (err) {
      console.error('Failed to load admin spotlights', err);
      toast.error('Failed to load spotlights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const filteredPlayers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return players;
    return players.filter(p => {
      const haystack = `${p.first_name || ''} ${p.last_name || ''} ${p.team_name || ''} ${p.division_name || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [players, searchQuery]);

  const selectedPlayer = useMemo(
    () => players.find(p => p.player_id === selectedPlayerId) || null,
    [players, selectedPlayerId]
  );

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image must be 5MB or smaller');
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await fileToBase64(file);
      setImagePreview(dataUrl);
      setImageData({ base64: dataUrl, filename: file.name, content_type: file.type || 'image/jpeg' });
    } catch (err) {
      console.error('Image read failed', err);
      toast.error('Could not read image file');
    }
  };

  const resetForm = () => {
    setSelectedPlayerId('');
    setWriteup('');
    setImagePreview(null);
    setImageData(null);
    setSearchQuery('');
  };

  const handlePublish = async () => {
    if (!selectedPlayerId) { toast.error('Please select a player from the roster'); return; }
    if (!writeup.trim()) { toast.error('Please add a short write-up'); return; }
    if (writeup.trim().length > 500) { toast.error('Write-up must be 500 characters or fewer'); return; }

    try {
      setSubmitting(true);
      const payload = {
        category,
        player_id: selectedPlayerId,
        writeup: writeup.trim(),
      };
      if (imageData) {
        payload.image_file_data = imageData.base64;
        payload.image_filename = imageData.filename;
        payload.image_content_type = imageData.content_type;
      }
      await axios.post(`${API_URL}/api/admin/spotlights`, payload, authConfig);
      toast.success(`${CATEGORIES.find(c => c.key === category)?.label} spotlight published`);
      resetForm();
      await fetchAll();
    } catch (err) {
      console.error('Publish failed', err);
      const detail = err?.response?.data?.detail || 'Failed to publish spotlight';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClear = async (cat) => {
    if (!window.confirm(`Clear the active ${CATEGORIES.find(c => c.key === cat)?.label} spotlight? This stops it from showing on the guest page.`)) return;
    try {
      setClearing(cat);
      await axios.delete(`${API_URL}/api/admin/spotlights/${cat}`, authConfig);
      toast.success('Spotlight cleared');
      await fetchAll();
    } catch (err) {
      console.error('Clear failed', err);
      toast.error('Failed to clear spotlight');
    } finally {
      setClearing(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-spotlights-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="back-to-dashboard-btn"
            aria-label="Back to admin dashboard"
          >
            <ArrowLeft className="w-5 h-5 text-navy" />
          </button>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Player Spotlights</h1>
            <p className="text-sm text-gray-500">Manage the 3 public Player Spotlight tiles on the guest page</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        {/* ACTIVE SPOTLIGHTS GRID */}
        <section data-testid="active-spotlights-grid">
          <div className="grid gap-4 sm:grid-cols-3">
            {CATEGORIES.map(({ key, label, accent, accentText, Icon }) => {
              const s = activeSpotlights[key];
              return (
                <div
                  key={key}
                  className="bg-white rounded-xl border-2 border-dashed border-gray-200 overflow-hidden"
                  data-testid={`active-spotlight-${key}`}
                >
                  <div className={`${accent} text-white px-4 py-2.5 flex items-center gap-2`}>
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
                  </div>
                  {s ? (
                    <div className="p-4">
                      {s.image_url ? (
                        <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 mb-3">
                          <img src={s.image_url} alt={s.player_display_name || label} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className={`w-full aspect-[4/3] rounded-lg bg-gray-100 mb-3 flex items-center justify-center ${accentText}`}>
                          <Trophy className="w-10 h-10 opacity-40" />
                        </div>
                      )}
                      <p className="font-display text-base font-black text-navy uppercase tracking-tight">{s.player_display_name || '—'}</p>
                      {(s.team_name || s.division_name) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[s.team_name, s.division_name].filter(Boolean).join(' • ')}
                        </p>
                      )}
                      {s.writeup && <p className="text-xs text-gray-600 mt-2 line-clamp-3">{s.writeup}</p>}
                      <button
                        onClick={() => handleClear(key)}
                        disabled={clearing === key}
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-red hover:underline disabled:opacity-50"
                        data-testid={`clear-spotlight-${key}-btn`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        {clearing === key ? 'Clearing…' : 'Clear active'}
                      </button>
                    </div>
                  ) : (
                    <div className="p-6 flex flex-col items-center justify-center text-center">
                      <Trophy className="w-10 h-10 text-gray-300 mb-2" />
                      <p className="text-sm font-semibold text-gray-400">No active spotlight</p>
                      <p className="text-xs text-gray-400 mt-0.5">Use the form below</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* NEW SPOTLIGHT FORM */}
        <section className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="new-spotlight-form">
          <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
            <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">New Spotlight</h2>
            <p className="text-xs text-gray-500 mt-0.5">Publishing replaces the current active spotlight for the chosen category.</p>
          </div>

          <div className="p-5 space-y-6">
            {/* Category */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Category <span className="text-red">*</span></label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {CATEGORIES.map(({ key, label, accent, Icon }) => {
                  const isSelected = category === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCategory(key)}
                      className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-xs font-bold uppercase tracking-wide transition-all ${
                        isSelected
                          ? `${accent} text-white border-transparent shadow`
                          : 'bg-white text-navy border-gray-200 hover:border-navy/30'
                      }`}
                      data-testid={`category-btn-${key}`}
                      aria-pressed={isSelected}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Player picker */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Player (from roster) <span className="text-red">*</span></label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by player name, team, or division…"
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy"
                  data-testid="player-search-input"
                />
              </div>

              {selectedPlayer ? (
                <div className="mt-3 flex items-center justify-between gap-3 p-3 bg-navy/5 border border-navy/20 rounded-lg" data-testid="selected-player-card">
                  <div className="min-w-0">
                    <p className="font-bold text-navy text-sm truncate">{selectedPlayer.first_name} {selectedPlayer.last_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {[selectedPlayer.team_name || 'Unrostered', selectedPlayer.division_name].filter(Boolean).join(' • ') || '—'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Public display: <span className="font-semibold text-gray-600">{selectedPlayer.display_name}</span></p>
                  </div>
                  <button
                    onClick={() => setSelectedPlayerId('')}
                    className="p-1.5 hover:bg-white rounded-md text-gray-500 hover:text-navy transition-colors"
                    aria-label="Clear selected player"
                    data-testid="clear-selected-player-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="mt-3 max-h-72 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100" data-testid="player-picker-list">
                  {loading ? (
                    <div className="p-4 text-center text-sm text-gray-400">Loading roster…</div>
                  ) : filteredPlayers.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-400">No matching players</div>
                  ) : (
                    filteredPlayers.slice(0, 100).map(p => (
                      <button
                        key={p.player_id}
                        type="button"
                        onClick={() => setSelectedPlayerId(p.player_id)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                        data-testid={`player-option-${p.player_id}`}
                      >
                        <p className="font-semibold text-sm text-navy">{p.first_name} {p.last_name}</p>
                        <p className="text-xs text-gray-500">
                          {[p.team_name || 'Unrostered', p.division_name].filter(Boolean).join(' • ') || '—'}
                        </p>
                      </button>
                    ))
                  )}
                  {filteredPlayers.length > 100 && (
                    <div className="px-3 py-2 text-xs text-gray-400 bg-gray-50">Showing first 100. Refine your search to narrow.</div>
                  )}
                </div>
              )}
            </div>

            {/* Write-up */}
            <div>
              <label htmlFor="writeup" className="text-xs font-bold uppercase tracking-wider text-gray-500">Short write-up <span className="text-red">*</span></label>
              <textarea
                id="writeup"
                value={writeup}
                onChange={(e) => setWriteup(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="One sentence celebrating this player's effort, attitude, or moment."
                className="mt-2 w-full p-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/20 focus:border-navy resize-none"
                data-testid="writeup-input"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{writeup.length}/500</p>
            </div>

            {/* Image */}
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Player photo <span className="text-gray-400">(optional)</span></label>
              <div className="mt-2 flex items-center gap-4">
                {imagePreview ? (
                  <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200">
                    <img src={imagePreview} alt="Spotlight preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => { setImagePreview(null); setImageData(null); }}
                      className="absolute top-1 right-1 p-1 bg-white/90 rounded-full hover:bg-white"
                      aria-label="Remove image"
                      data-testid="remove-image-btn"
                    >
                      <X className="w-3.5 h-3.5 text-navy" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-300">
                    <Camera className="w-7 h-7" />
                  </div>
                )}
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-navy hover:bg-gray-50 cursor-pointer" data-testid="upload-image-trigger">
                  <Camera className="w-4 h-4" />
                  {imagePreview ? 'Replace photo' : 'Upload photo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleImageChange} data-testid="image-upload-input" />
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-1">JPG/PNG/WebP, max 5MB. If omitted, the public tile shows a clean fallback.</p>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <Button
                type="button"
                variant="ghost"
                onClick={resetForm}
                disabled={submitting}
                data-testid="reset-form-btn"
              >
                Reset
              </Button>
              <Button
                type="button"
                onClick={handlePublish}
                disabled={submitting || !selectedPlayerId || !writeup.trim()}
                className="bg-navy hover:bg-navy/90 text-white"
                data-testid="publish-spotlight-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {submitting ? 'Publishing…' : 'Publish Spotlight'}
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminSpotlightsPage;
